import TableChartIcon from '@mui/icons-material/TableChart';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ColumnItem } from './TableListPsim';

interface ColumnConfigsProps<T extends object> {
    id: string;
    columns: ColumnItem<T>[];
    loadConfigColumns: () => void;
}

export interface ConfigColumn {
    id: string;
    visible: boolean;
}

function ColumnConfigs<T extends object>({ id, columns: initialColumns, loadConfigColumns }: ColumnConfigsProps<T>) {
    const [open, setOpen] = useState(false);
    const [columns, setColumns] = useState<ColumnItem<T>[]>(initialColumns);

    useEffect(() => {
        setColumns(initialColumns);
    }, [initialColumns]);

    function setColumnVisible(id: string, visible: boolean) {
        setColumns((o) => {
            const newColumns = [...o];
            const index = newColumns.findIndex((c) => c.field === id);
            if (index !== -1) {
                newColumns[index] = { ...newColumns[index], visible };
            }
            return newColumns;
        });
    }

    const handleOpen = async () => {
        setColumns(initialColumns);
        setOpen(true);
    };

    return (
        <>
            <Tooltip title={'Configurar colunas'}>
                <IconButton
                    size="small"
                    onClick={handleOpen}
                >
                    <TableChartIcon />
                </IconButton>
            </Tooltip>

            <Dialog open={open} onClose={(e, reason) => {
                setOpen(false);
            }} fullWidth maxWidth="xs" disableEscapeKeyDown>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <TableChartIcon fontSize='medium' sx={{ marginRight: 2, color: 'rgb(4, 90, 142)' }} />Configuração das colunas
                </DialogTitle>
                <DialogContent dividers sx={{ paddingBottom: 1 }}>
                    <DndContext onDragEnd={(event) => {
                        const { active, over } = event;
                        if (!over) return;
                        if (active.id !== over.id) {
                            const oldIndex = columns.findIndex((c) => c.field === active.id);
                            const newIndex = columns.findIndex((c) => c.field === over.id);

                            setColumns((o) => arrayMove(o, oldIndex, newIndex));
                        }
                    }}>
                        <SortableContext items={columns.map(c => c.field)}>
                            <Box sx={{ overflow: 'hidden' }}>
                                {columns.filter(c => c.field !== 'id').map((item, index) => {
                                    return (
                                        <SortableItem key={item.field} item={item} index={index} setColumnVisible={setColumnVisible} />
                                    );
                                })}
                            </Box>
                        </SortableContext>
                    </DndContext>
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => {
                            setOpen(false);
                        }}
                    >
                        Fechar
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            saveColumnsConfigsToLocalStorage(id, columns.map(c => ({ id: c.field, visible: c.visible })));
                            loadConfigColumns();
                            setOpen(false);
                        }}
                    >
                        Confirmar
                    </Button>                    
                </DialogActions>
            </Dialog>
        </>
    );
}

function SortableItem<T extends object>({ index, item, setColumnVisible }: { item: ColumnItem<T>, index: number, setColumnVisible: (id: string, visible: boolean) => void }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: item.field });

    const style = {
        transform: CSS.Transform.toString(transform ?? {
            x: 0,
            y: 0,
            scaleX: 1,
            scaleY: 1
        }),
        transition,
    };

    return (
        <Box ref={setNodeRef} style={style} {...attributes} sx={{ display: 'flex', alignItems: 'center', padding: 0, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: '5px', marginBottom: '5px' }}>
            <Checkbox
                sx={{ padding: 1, margin: 1, pointerEvents: 'auto' }}
                size='small'
                checked={item.visible !== false}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                onChange={(e) => {
                    setColumnVisible(item.field, e.target.checked);
                }}
            />
            <Box {...listeners} sx={{ flex: 1, cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>{item.value}</Box>
                <DragIndicatorIcon />
            </Box>
        </Box>
    );
}

export function saveColumnsConfigsToLocalStorage(key: string, filters: any) {
    localStorage.setItem('columns_' + key, JSON.stringify(filters));
}

export function loadColumnsConfigsFromLocalStorage(key: string) {
    const filtersJson = localStorage.getItem('columns_' + key);
    if (filtersJson) {
        return JSON.parse(filtersJson);
    }
    return [];
}

export default ColumnConfigs;